// =============================================================================
// EMAIL RICH EDITOR — éditeur WYSIWYG pour templates email métier
// =============================================================================
//
// Objectif : permettre à un utilisateur NON-technique (ex. Solène / OMV) de
// composer un email mis en forme (titres, gras, listes, liens, variables)
// SANS écrire de HTML. La sortie `onChange(html)` est du HTML propre généré
// par TipTap (getHTML), prêt à être stocké dans `email_templates.html_body`.
//
// Réutilise le stack TipTap déjà présent (cf. SmartLegalEditor.jsx) — aucune
// nouvelle dépendance lourde. Extensions toutes déjà installées dans
// node_modules/@tiptap/* (starter-kit, underline, text-align, link…).
//
// Variables : insertion de jetons `{{cle}}` (convention déjà gérée par
// l'aperçu/rendu backend, email_config.render_template).
// =============================================================================

import React, { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Node, mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Image as ImageIcon,
  Loader2,
  Undo,
  Redo,
  Braces,
  ChevronDown,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

// Node image MINIMAL (dépendance-free : réutilise @tiptap/core déjà installé, pas
// de @tiptap/extension-image à ajouter). Rend une balise <img src …> propre,
// compatible email (src absolu hébergé + style max-width). parseHTML : recharge
// les <img> d'un template existant à l'édition (sinon le schéma les supprimerait).
const EmailImage = Node.create({
  name: "image",
  group: "block",
  draggable: true,
  selectable: true,
  atom: true,
  addAttributes() {
    return {
      src: { default: null },
      alt: { default: null },
      style: { default: "max-width:220px;height:auto;" },
    };
  },
  parseHTML() {
    return [{ tag: "img[src]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(HTMLAttributes)];
  },
});
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { cn } from "../../lib/utils";
import { enteteTenant } from "../../utils/enteteTenant";

// Thème (champ `source` des variables) → libellé. Copie de la table
// VariablesManager.jsx (SOURCES) pour grouper le menu « Variable » sans importer
// le gros composant page. L'ordre ci-dessous pilote l'ordre des groupes.
export const VARIABLE_THEME_LABELS = {
  client: "Client",
  entreprise: "Entreprise",
  dossier: "Dossier",
  logement: "Logement",
  devis: "Devis",
  aides: "Aides",
  operation: "Opération/Deal",
  installateur: "Installateur",
  chantier: "Chantier",
  reglementaire: "Réglementaire CEE",
  pac: "PAC (BAR-TH-171)",
  cet: "CET (BAR-TH-148)",
  signature: "Signature/Légal",
  calcule: "Calculée",
};

// Variables proposées par défaut (insérables en 1 clic). L'utilisateur peut
// aussi taper d'autres jetons {{xxx}} à la main : ils seront détectés au save.
export const DEFAULT_EMAIL_VARIABLES = [
  { key: "nom_beneficiaire", label: "Nom du bénéficiaire" },
  { key: "prenom", label: "Prénom" },
  { key: "nom_entreprise", label: "Nom de l'entreprise" },
  { key: "numero_devis", label: "N° de devis" },
  { key: "montant", label: "Montant" },
  { key: "date", label: "Date" },
  { key: "lien", label: "Lien" },
];

function ToolButton({ onClick, isActive, icon: Icon, title }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cn("h-8 w-8 p-0 hover:bg-[var(--oh-bg-elevated)]", isActive && "bg-[var(--oh-bg-elevated)] text-[var(--oh-text)]")}
      title={title}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}

function Toolbar({ editor, variables, onInsertVariable, onUploadImage, onImageByUrl, uploadingImg }) {
  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link")?.href || "";
    // eslint-disable-next-line no-alert
    const url = window.prompt("URL du lien (laisser vide pour retirer)", previous);
    if (url === null) return; // annulé
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  // Groupement par thème SI au moins une variable porte un `theme` (variables
  // réelles documents-engine). Sinon liste à plat (DEFAULT_EMAIL_VARIABLES et
  // autres usages) → aucune régression.
  const groupedVars = useMemo(() => {
    const vars = variables || [];
    const hasThemes = vars.some((v) => v && v.theme);
    if (!hasThemes) return null;
    const byTheme = new Map();
    for (const v of vars) {
      const t = v.theme || "_autres";
      if (!byTheme.has(t)) byTheme.set(t, []);
      byTheme.get(t).push(v);
    }
    const ordered = Object.keys(VARIABLE_THEME_LABELS).filter((t) => byTheme.has(t));
    const extras = [...byTheme.keys()].filter((t) => !ordered.includes(t) && t !== "_autres");
    const tail = byTheme.has("_autres") ? ["_autres"] : [];
    return [...ordered, ...extras, ...tail].map((t) => ({
      theme: t,
      label: VARIABLE_THEME_LABELS[t] || (t === "_autres" ? "Autres" : t),
      items: byTheme.get(t),
    }));
  }, [variables]);

  if (!editor) return null;

  return (
    <div className="flex items-center gap-1 p-2 border-b border-[var(--oh-border)] bg-[var(--oh-bg-elevated)] rounded-t-lg flex-wrap">
      <ToolButton onClick={() => editor.chain().focus().undo().run()} icon={Undo} title="Annuler" />
      <ToolButton onClick={() => editor.chain().focus().redo().run()} icon={Redo} title="Rétablir" />
      <div className="w-px h-6 bg-slate-600 mx-1" />
      <ToolButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive("bold")} icon={Bold} title="Gras" />
      <ToolButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive("italic")} icon={Italic} title="Italique" />
      <ToolButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive("underline")} icon={UnderlineIcon} title="Souligné" />
      <div className="w-px h-6 bg-slate-600 mx-1" />
      <ToolButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive("heading", { level: 2 })} icon={Heading2} title="Grand titre" />
      <ToolButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive("heading", { level: 3 })} icon={Heading3} title="Sous-titre" />
      <div className="w-px h-6 bg-slate-600 mx-1" />
      <ToolButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive("bulletList")} icon={List} title="Liste à puces" />
      <ToolButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive("orderedList")} icon={ListOrdered} title="Liste numérotée" />
      <div className="w-px h-6 bg-slate-600 mx-1" />
      <ToolButton onClick={() => editor.chain().focus().setTextAlign("left").run()} isActive={editor.isActive({ textAlign: "left" })} icon={AlignLeft} title="Aligner à gauche" />
      <ToolButton onClick={() => editor.chain().focus().setTextAlign("center").run()} isActive={editor.isActive({ textAlign: "center" })} icon={AlignCenter} title="Centrer" />
      <ToolButton onClick={() => editor.chain().focus().setTextAlign("right").run()} isActive={editor.isActive({ textAlign: "right" })} icon={AlignRight} title="Aligner à droite" />
      <ToolButton onClick={setLink} isActive={editor.isActive("link")} icon={LinkIcon} title="Insérer / modifier un lien" />
      {/* Insertion image / logo (signature) */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 hover:bg-[var(--oh-bg-elevated)] gap-1"
            title="Insérer une image / un logo"
            data-testid="email-editor-insert-image"
            disabled={uploadingImg}
          >
            {uploadingImg ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            <span className="text-xs">Image</span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <DropdownMenuItem onClick={onUploadImage} className="hover:bg-[var(--oh-bg-elevated)] cursor-pointer text-sm text-[var(--oh-text)]" data-testid="email-editor-image-upload">
            Téléverser un logo (PNG/JPG…)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onImageByUrl} className="hover:bg-[var(--oh-bg-elevated)] cursor-pointer text-sm text-[var(--oh-text)]" data-testid="email-editor-image-url">
            Insérer par URL (https://…)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <div className="w-px h-6 bg-slate-600 mx-1" />
      {/* Insertion de variables */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 hover:bg-[var(--oh-bg-elevated)] gap-1 text-[var(--oh-accent-text)]"
            title="Insérer une variable"
            data-testid="email-editor-insert-var"
          >
            <Braces className="h-4 w-4" />
            <span className="text-xs">Variable</span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] max-h-80 overflow-y-auto">
          <DropdownMenuLabel className="text-[var(--oh-text-secondary)] text-xs">
            Insérer une variable
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-[var(--oh-bg-elevated)]" />
          {groupedVars
            ? groupedVars.map((g) => (
                <div key={g.theme}>
                  <DropdownMenuLabel className="text-[var(--oh-accent-text)] text-[10px] uppercase tracking-wide pt-2">
                    {g.label}
                  </DropdownMenuLabel>
                  {g.items.map((v) => (
                    <DropdownMenuItem
                      key={`${g.theme}-${v.key}`}
                      onClick={() => onInsertVariable(v.key)}
                      className="hover:bg-[var(--oh-bg-elevated)] cursor-pointer text-sm"
                    >
                      <span className="text-[var(--oh-text)] truncate">{v.label}</span>
                      <code className="ml-2 text-xs text-[var(--oh-accent-text)] shrink-0">{`{{${v.key}}}`}</code>
                    </DropdownMenuItem>
                  ))}
                </div>
              ))
            : (variables || []).map((v) => (
                <DropdownMenuItem
                  key={v.key}
                  onClick={() => onInsertVariable(v.key)}
                  className="hover:bg-[var(--oh-bg-elevated)] cursor-pointer text-sm"
                >
                  <span className="text-[var(--oh-text)]">{v.label}</span>
                  <code className="ml-2 text-xs text-[var(--oh-accent-text)]">{`{{${v.key}}}`}</code>
                </DropdownMenuItem>
              ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function EmailRichEditor({
  value = "",
  onChange,
  variables = DEFAULT_EMAIL_VARIABLES,
  placeholder = "Composez votre email ici…",
  minHeight = "260px",
  className,
  tenantId,
}) {
  const fileRef = useRef(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Placeholder.configure({ placeholder }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({ openOnClick: false, autolink: true }),
      EmailImage,
    ],
    content: value,
    editorProps: {
      attributes: {
        class: "prose prose-sm prose-invert max-w-none focus:outline-none p-4",
        style: `min-height: ${minHeight}`,
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange?.(html === "<p></p>" ? "" : html);
    },
  });

  // Synchroniser une valeur externe (reset à l'ouverture du dialog).
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      if (value || editor.getHTML() !== "<p></p>") {
        editor.commands.setContent(value || "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const insertVariable = useCallback(
    (key) => {
      if (!editor) return;
      editor.chain().focus().insertContent(`{{${key}}}`).run();
    },
    [editor]
  );

  // Insère une image. L'URL est rendue ABSOLUE (window.location.origin) → src
  // hébergé chargeable depuis un email (jamais un blob local).
  const insertImageUrl = useCallback(
    (rawUrl) => {
      if (!editor || !rawUrl) return;
      const abs = /^https?:\/\//i.test(rawUrl)
        ? rawUrl
        : `${window.location.origin}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`;
      editor.chain().focus().insertContent({
        type: "image",
        attrs: { src: abs, alt: "logo", style: "max-width:220px;height:auto;" },
      }).run();
    },
    [editor]
  );

  const uploadImage = useCallback(
    async (fileObj) => {
      if (!fileObj || !editor) return;
      setUploadingImg(true);
      try {
        const token = localStorage.getItem("session_token");
        const fd = new FormData();
        fd.append("file", fileObj);
        const res = await fetch(`${API}/api/governance/email-config/upload-image`, {
          method: "POST",
          credentials: "include",
          headers: {
            ...enteteTenant(tenantId || localStorage.getItem("current_tenant_slug")),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: fd,
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(typeof e.detail === "string" ? e.detail : `HTTP ${res.status}`);
        }
        const data = await res.json();
        insertImageUrl(data.url);
      } catch (e) {
        // eslint-disable-next-line no-alert
        window.alert(`Échec de l'upload de l'image : ${e.message || e}`);
      } finally {
        setUploadingImg(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    },
    [editor, tenantId, insertImageUrl]
  );

  const onImageByUrl = useCallback(() => {
    // eslint-disable-next-line no-alert
    const url = window.prompt("URL de l'image (logo hébergé, https://…)");
    if (url) insertImageUrl(url);
  }, [insertImageUrl]);

  return (
    <div className={cn("space-y-1", className)} data-testid="email-rich-editor">
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => uploadImage(e.target.files?.[0])}
        data-testid="email-editor-image-input"
      />
      <div className="border border-[var(--oh-border)] rounded-lg overflow-hidden bg-[var(--oh-bg-surface)]">
        <Toolbar
          editor={editor}
          variables={variables}
          onInsertVariable={insertVariable}
          onUploadImage={() => fileRef.current?.click()}
          onImageByUrl={onImageByUrl}
          uploadingImg={uploadingImg}
        />
        <EditorContent editor={editor} />
      </div>
      <p className="text-xs text-[var(--oh-text-muted)]">
        Mise en forme visuelle — le HTML est généré automatiquement. Utilisez «&nbsp;Variable&nbsp;» pour insérer
        des champs dynamiques comme <code className="text-[var(--oh-accent-text)]">{`{{nom_beneficiaire}}`}</code>.
      </p>
    </div>
  );
}
